import * as THREE from "three";
import { defineModel, type Part } from "../../lib";

// The player avatar from Age of MCP — a tapered body in the player's own colour
// under a round head, deliberately near-symmetric so it needs no facing. About
// 1.75 m tall (0.35 tiles at 5 m per tile). Ported 1:1 from the game's
// `playerParts`; the geometry is preserved exactly.
//
// In the game the body colour is the player's own (the owner colour). Here the
// body parts are tagged `slot: "team"`, so `COLOR` is only the DEFAULT: a
// consumer recolours the whole avatar to any team colour at build time via
// `instantiateModel(model, { slots: { team: "#e74c3c" } })` (the viewer's colour
// picker, the preview's `--team`). The tool, head and bare shins are not team-
// coloured, matching the game.
//
// Variants capture the game's avatar states: `default` standing, the transient
// HELD-TOOL poses swapped in during a harvest (`axe` while chopping a tree,
// `pick` while mining), and the SEATED variant the avatar layer swaps in once a
// sit pose passes its half (origin at the seat contact, body settled, short bent
// legs hanging forward-down). The carried-good overlay (a typed resource cradled
// at the chest) is omitted here: it is parametric over every good id and depends
// on the game's `goods` module, which a model file may not import.
const COLOR = "#3b7fd0";    // default team colour (the `team` slot's default; per-player in game)
const HEAD = "#f1deb5";
const HANDLE = "#6b5740";   // wooden tool handle
const METAL = "#8a8580";    // tool head / blade
const SHIN = "#4a3c2c";     // bare shins on the seated pose

type Tool = "axe" | "pick";

// A transient harvest tool baked at the avatar's side: an axe while chopping,
// a pick while mining, so a chop reads as a chop.
function toolParts(tool: Tool): Part[] {
  if (tool === "axe") {
    return [
      { geometry: new THREE.CylinderGeometry(0.014, 0.014, 0.3, 5), color: HANDLE, position: [0.13, 0.18, 0.03], rotation: [0, 0, 0.2] },
      { geometry: new THREE.BoxGeometry(0.065, 0.095, 0.018), color: METAL, position: [0.185, 0.305, 0.03] },
    ];
  }
  return [
    { geometry: new THREE.CylinderGeometry(0.014, 0.014, 0.32, 5), color: HANDLE, position: [0.13, 0.19, 0.03], rotation: [0.2, 0, 0.25] },
    { geometry: new THREE.BoxGeometry(0.17, 0.026, 0.026), color: METAL, position: [0.125, 0.345, 0.065], rotation: [0, 0, 0.1] },
  ];
}

// The avatar: a tapered body + round head. `seated` swaps in the sitting pose
// (shorter body, bent legs hanging forward); `tool` bakes a held harvest tool.
function avatar(seated: boolean, tool?: Tool): Part[] {
  const legs: Part[] = seated
    ? [
        { geometry: new THREE.BoxGeometry(0.045, 0.042, 0.13), color: COLOR, position: [-0.036, 0.015, 0.07], slot: "team" },
        { geometry: new THREE.BoxGeometry(0.045, 0.042, 0.13), color: COLOR, position: [0.036, 0.015, 0.07], slot: "team" },
        { geometry: new THREE.BoxGeometry(0.042, 0.13, 0.042), color: SHIN, position: [-0.036, -0.06, 0.12] },
        { geometry: new THREE.BoxGeometry(0.042, 0.13, 0.042), color: SHIN, position: [0.036, -0.06, 0.12] },
      ]
    : [];
  return [
    ...legs,
    ...(seated || !tool ? [] : toolParts(tool)),
    { geometry: new THREE.CylinderGeometry(0.06, 0.085, seated ? 0.19 : 0.22, 7),
      color: COLOR, position: [0, seated ? 0.095 : 0.11, 0], slot: "team" },
    { geometry: new THREE.IcosahedronGeometry(0.062, 0),
      color: HEAD, position: [0, seated ? 0.255 : 0.285, 0] },
  ];
}

export const player = defineModel({
  id: "player",
  batchId: "characters",
  name: "Player",
  tags: ["character", "avatar", "humanoid", "person", "player"],
  summary: "The Age of MCP player avatar — a tapered body under a round head.",
  description:
    "The player's own avatar in the game: a near-symmetric tapered body in the " +
    "player's colour under a round head. Variants cover the standing pose, the " +
    "transient axe/pick held while harvesting, and the seated pose. Ported from " +
    "age-of-mcp's playerParts.",
  author: "age-of-mcp",
  footprint: 0.11,
  variants: [
    { id: "default", label: "Standing", description: "The default standing avatar.", parts: () => avatar(false) },
    { id: "axe", label: "With axe", description: "Holding an axe while chopping a tree.", parts: () => avatar(false, "axe") },
    { id: "pick", label: "With pick", description: "Holding a pick while mining stone or ore.", parts: () => avatar(false, "pick") },
    { id: "seated", label: "Seated", description: "The sitting pose, settled with legs hanging forward.", parts: () => avatar(true) },
  ],
  // Where a carried good is cradled in front of the chest — the same anchor the
  // game uses for `heldGoodAt`. The good itself is supplied by the consumer at
  // bake time (`bakeSockets`), so the avatar carries wood, grain or ore without
  // ever importing a good.
  sockets: [
    { id: "carry", position: [0, 0.14, 0.13], slotType: "carry", description: "A good cradled in front of the chest." },
  ],
  animations: [],
});
